import useInquiryQueueCount from '@/hooks/useInquiryQueueCount';
import { usePaymentDeviceBadgeTotal } from '@/hooks/usePaymentDeviceRequestsPendingCount';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { LEAD_COORDINATORS_TO_REVIEW_URL, LeadCoordinatorToReviewDtoOut } from '@/models/out/LeadCoordinatorToReviewDtoOut';
import { COORDINATOR_ADD_REQUESTS_URL, CoordinatorAddRequestDtoOut } from '@/models/out/CoordinatorAddRequestDtoOut';
import {
  ISRAELI_ID_CHANGE_REQUESTS_URL,
  SupportedIsraeliIdChangeRequestDtoIn,
} from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';

/**
 * Badge counts for the management-area sidebar. Unlike SidebarBadgeProvider this
 * is fund-independent, so it works outside FundProvider (the /management routes).
 *
 * Called once per nav item, so it must stay a plain reader: everything with a side effect -
 * the chime, the notification, the tab title - lives in InquiryQueueAlert, which is mounted
 * once at the app root. The queue itself is fetched by useInquiryQueueCount and shared
 * through the react-query cache, so the extra callers cost no extra requests.
 */
export default function useManagementBadges(): Record<string, number> {
  const { count } = useInquiryQueueCount();
  // Requests waiting for approval plus devices out 3 months or more (HAT-1434).
  const deviceBadge = usePaymentDeviceBadgeTotal();
  // HAT-1428: a coordinator's request to correct a supported's ID waits for the office, and
  // nothing else says it arrived. The «נתמכים» board's own query - one cache entry, so a decision
  // there clears the badge too.
  const { data: idChangeRequests } = useGetAuthApi<SupportedIsraeliIdChangeRequestDtoIn[]>(
    ISRAELI_ID_CHANGE_REQUESTS_URL,
    undefined,
    { staleTime: 30000, refetchInterval: 60000 }
  );

  // Lead coordinators held back by an Israeli ID on a record under another name - the page's own query.
  const isPeopleOn = useFeature('fund-representatives');
  const { data: leadsToReview } = useGetAuthApi<LeadCoordinatorToReviewDtoOut[]>(
    isPeopleOn ? LEAD_COORDINATORS_TO_REVIEW_URL : '',
    undefined,
    { staleTime: 30000, refetchInterval: 60000 }
  );

  // A lead coordinator's request to add a coordinator (SPR-10486) - the page's own query.
  const { data: addRequests } = useGetAuthApi<CoordinatorAddRequestDtoOut[]>(
    isPeopleOn ? COORDINATOR_ADD_REQUESTS_URL : '',
    undefined,
    { staleTime: 30000, refetchInterval: 60000 }
  );

  return {
    managementInquiries: count ?? 0,
    managementPaymentDeviceRequests: deviceBadge ?? 0,
    israeliIdChangeRequests: idChangeRequests?.length ?? 0,
    leadCoordinatorsReview: leadsToReview?.length ?? 0,
    coordinatorAddRequests: addRequests?.length ?? 0,
  };
}
